import React from 'react';
import { withAgentCompanyQuery } from '../agentCompanyQuery';
import DodTermText from '../DodTermText';
import './FeatureAgentGates.css';

type PermissionCatalogEntry = {
  id: string;
  label_en?: string;
  label_es?: string;
  modes?: string[];
  features?: string[];
};

type AgentCredential = {
  portal_id: string;
  label?: string;
  username?: string;
  has_secret?: boolean;
};

type FeatureKey =
  | 'cloud-compliance'
  | 'dod-contracts-compliance'
  | 'ofac-compliance'
  | 'website-compliance'
  | 'business-creation-compliance';

type MonitorRun = {
  run_id: string;
  skill: string;
  status: string;
  title?: string;
  summary?: string;
  decision?: string;
  draft?: {
    openConsole?: string;
    openTask?: string;
    kind?: string;
    autonomy?: string;
    proposals?: Array<{ title?: string; action?: string }>;
    findings?: Array<{ id?: string; label?: string; state?: string; note?: string }>;
  };
};

const FEATURE_SKILL: Record<FeatureKey, string> = {
  'cloud-compliance': 'cloud_review',
  'dod-contracts-compliance': 'dod_gap',
  'ofac-compliance': 'ofac_watch',
  'website-compliance': 'website_scan',
  'business-creation-compliance': 'fleet_discovery',
};

/** Hide drafts owned by the task card or empty "Run review" placeholders. */
export const isAgentInboxWorthy = (run: MonitorRun): boolean => {
  const draft = run.draft || {};
  if (draft.kind === 'task_actions') return false;
  const findings = Array.isArray(draft.findings) ? draft.findings : [];
  const proposals = Array.isArray(draft.proposals) ? draft.proposals : [];
  if (draft.autonomy === 'draft' && findings.length === 0 && proposals.length === 0) {
    return false;
  }
  return true;
};

const checklistOpenTask = (run: MonitorRun): string => {
  const openTask = String(run.draft?.openTask || '').trim();
  if (!openTask || openTask === 'person-reviews-smart-solutions') return '';
  return openTask;
};

const shortSummary = (value: string, max = 220): string => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
};

type Props = {
  feature: FeatureKey;
  language: 'en' | 'es';
  gatewayBaseUrl: string;
  accessToken: string;
  /** Console account picker — scopes gates / drafts to that company. */
  companyId?: number | null;
  readOnly?: boolean;
  /** When set, open Gates and credentials and prefill the portal id. */
  focusPortalId?: string;
  /** Jump to a checklist control when a draft points at one. */
  onOpenTask?: (controlId: string) => void;
  /** Open Folders & Sources in the console chrome (no navigate away). */
  onOpenSources?: () => void;
};

/**
 * Agent gates + decisions for one feature console.
 * The Agent install page is install/devices only — not this inbox.
 * Do-it-for-me approvals stay on the task card; this inbox is for
 * smart scans, cloud/OFAC findings, and credential-gated work.
 */
export default function FeatureAgentGates({
  feature,
  language,
  gatewayBaseUrl,
  accessToken,
  companyId = null,
  readOnly = false,
  focusPortalId = '',
  onOpenTask,
  onOpenSources,
}: Props): React.ReactElement | null {
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const skill = FEATURE_SKILL[feature];
  const showCredentials = (
    feature === 'dod-contracts-compliance'
    || feature === 'website-compliance'
    || feature === 'cloud-compliance'
  );
  const [permissions, setPermissions] = React.useState<Record<string, string>>({});
  const [catalog, setCatalog] = React.useState<PermissionCatalogEntry[]>([]);
  const [credentials, setCredentials] = React.useState<AgentCredential[]>([]);
  const [awaiting, setAwaiting] = React.useState<MonitorRun[]>([]);
  const [recent, setRecent] = React.useState<MonitorRun[]>([]);
  const [portalId, setPortalId] = React.useState('sprs');
  const [portalUser, setPortalUser] = React.useState('');
  const [portalSecret, setPortalSecret] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [deviceCount, setDeviceCount] = React.useState(0);
  const [onlineCount, setOnlineCount] = React.useState(0);
  const [gatesOpen, setGatesOpen] = React.useState(false);
  const sectionRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const portal = String(focusPortalId || '').trim();
    if (!portal) return;
    setPortalId(portal);
    setGatesOpen(true);
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [focusPortalId]);

  const text = language === 'es'
    ? {
      title: 'Agente en esta área',
      needsTitle: 'Necesita su decisión',
      needsEmpty: 'Nada espera una decisión en esta área. Use «Hazlo por mí» en cada paso para trabajo con aprobación.',
      run: 'Buscar soluciones',
      running: 'Buscando…',
      approve: 'Aprobar',
      reject: 'Rechazar',
      dismiss: 'Descartar',
      openTask: 'Abrir paso',
      recentTitle: 'Reciente',
      gatesTitle: 'Puertas humanas',
      gatesHelp: 'Cerrado por defecto. Tras aprobar, se ejecutan las acciones activadas.',
      save: 'Guardar puertas',
      saving: 'Guardando…',
      modeOff: 'Apagado',
      modeApproval: 'Tras aprobación',
      modeAuto: 'Automático',
      credTitle: 'Credenciales de portal y conector',
      credHelp: 'Secretos para portales (sprs, sam) y claves de conectores (tx_comptroller). Si «Hazlo por mí» pide autenticación, agréguela aquí y vuelva a ejecutar.',
      credPortal: 'Id de portal / conector',
      credUser: 'Usuario (opcional para claves API)',
      credSecret: 'Secreto / contraseña / clave API',
      credSave: 'Guardar credencial',
      credSaved: 'Credenciales guardadas',
      saved: 'Puertas guardadas.',
      runOk: 'Propuestas listas arriba.',
      loadError: 'No se pudo cargar el agente de esta área.',
      saveError: 'No se pudieron guardar las puertas.',
      runError: 'No se pudo buscar soluciones.',
      decideError: 'No se pudo registrar la decisión.',
      stripOnline: 'Agente local en línea',
      stripOffline: 'Agente local sin dispositivos',
      stripIdle: 'Agente instalado, sin check-in reciente',
      stripHelp: 'Apruebe aquí los borradores del agente. El trabajo «Hazlo por mí» se aprueba en el paso.',
      stripDevices: 'dispositivos',
      stripWaiting: 'en espera',
      openInstall: 'Instalar / dispositivos',
      foldersLink: 'Fuentes',
      foldersHelp: 'Fuentes conecta dónde puede vivir la prueba (Este Mac, Dropbox, Correo). La búsqueda y las aprobaciones quedan en esta franja — abra Fuentes solo para conectar lugares.',
    }
    : {
      title: 'Agent on this feature',
      needsTitle: 'Needs your decision',
      needsEmpty: 'Nothing waiting here. Use Do it for me on a step for approval-gated work.',
      run: 'Find solutions',
      running: 'Searching…',
      approve: 'Approve',
      reject: 'Reject',
      dismiss: 'Dismiss',
      openTask: 'Open step',
      recentTitle: 'Recent',
      gatesTitle: 'Human gates',
      gatesHelp: 'Fail-closed by default. After you approve, enabled actions run.',
      save: 'Save gates',
      saving: 'Saving…',
      modeOff: 'Off',
      modeApproval: 'After approval',
      modeAuto: 'Automatic',
      credTitle: 'Portal & connector credentials',
      credHelp: 'Vaulted secrets for portals (sprs, sam) and connector keys (tx_comptroller). When Do it for me hits auth, add the credential here and re-run.',
      credPortal: 'Portal / connector id',
      credUser: 'Username (optional for API keys)',
      credSecret: 'Secret / password / API key',
      credSave: 'Save credential',
      credSaved: 'Saved credentials',
      saved: 'Gates saved.',
      runOk: 'Proposals ready above.',
      loadError: 'Could not load the agent for this feature.',
      saveError: 'Could not save gates.',
      runError: 'Could not find solutions.',
      decideError: 'Could not record the decision.',
      stripOnline: 'Local agent online',
      stripOffline: 'Local agent — no devices yet',
      stripIdle: 'Agent installed, no recent check-in',
      stripHelp: 'Approve agent drafts here. Do-it-for-me work is approved on the step.',
      stripDevices: 'devices',
      stripWaiting: 'waiting',
      openInstall: 'Install / devices',
      foldersLink: 'Sources',
      foldersHelp: 'Sources connects where proof can live (This Mac, Dropbox, Email). Search and approvals stay on this strip — open Sources only to connect places.',
    };

  const load = React.useCallback(async () => {
    if (!accessToken || !gateway) {
      setLoaded(true);
      return;
    }
    try {
      const [permRes, monitorRes] = await Promise.all([
        fetch(
          `${gateway}${withAgentCompanyQuery(`/api/v1/agent/monitor/permissions?feature=${encodeURIComponent(feature)}`, companyId)}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        ),
        fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/monitor', companyId)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);
      if (permRes.ok) {
        const body = await permRes.json() as {
          permissions?: Record<string, string>;
          catalog?: PermissionCatalogEntry[];
          credentials?: AgentCredential[];
        };
        setPermissions(body.permissions && typeof body.permissions === 'object' ? body.permissions : {});
        setCatalog(Array.isArray(body.catalog) ? body.catalog : []);
        setCredentials(Array.isArray(body.credentials) ? body.credentials : []);
      }
      if (monitorRes.ok) {
        const body = await monitorRes.json() as {
          awaiting?: MonitorRun[];
          recent?: MonitorRun[];
        };
        const forFeature = (run: MonitorRun) => run.skill === skill && isAgentInboxWorthy(run);
        setAwaiting((Array.isArray(body.awaiting) ? body.awaiting : []).filter(forFeature));
        setRecent(
          (Array.isArray(body.recent) ? body.recent : [])
            .filter((run) => run.skill === skill)
            .slice(0, 6),
        );
      }
      try {
        const statusRes = await fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/status', companyId)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (statusRes.ok) {
          const statusBody = await statusRes.json() as {
            devices?: Array<{ online?: boolean; last_seen_at?: string; last_checkin_at?: string }>;
          };
          const devices = Array.isArray(statusBody.devices) ? statusBody.devices : [];
          setDeviceCount(devices.length);
          setOnlineCount(devices.filter((device) => device.online !== false && (device.last_seen_at || device.last_checkin_at)).length);
        }
      } catch {
        // Status is optional chrome; gates still load.
      }
      if (!permRes.ok && !monitorRes.ok) {
        setError(text.loadError);
      } else {
        setError('');
      }
    } catch {
      setError(text.loadError);
    } finally {
      setLoaded(true);
    }
  }, [accessToken, companyId, feature, gateway, skill, text.loadError]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const savePermissions = async () => {
    if (readOnly || !accessToken) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const owned = Object.fromEntries(
        catalog.map((entry) => [entry.id, permissions[entry.id] || 'off']),
      );
      const response = await fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/monitor/permissions', companyId)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ permissions: owned }),
      });
      const body = await response.json() as { permissions?: Record<string, string>; detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : text.saveError);
        return;
      }
      if (body.permissions) setPermissions(body.permissions);
      setMessage(text.saved);
    } catch {
      setError(text.saveError);
    } finally {
      setBusy(false);
    }
  };

  const saveCredential = async () => {
    if (readOnly || !accessToken || !showCredentials) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/monitor/credentials', companyId)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portal_id: portalId.trim(),
          username: portalUser.trim(),
          secret: portalSecret,
        }),
      });
      const body = await response.json() as { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : text.saveError);
        return;
      }
      setPortalSecret('');
      setMessage(language === 'es' ? 'Credencial guardada.' : 'Credential saved.');
      await load();
    } catch {
      setError(text.saveError);
    } finally {
      setBusy(false);
    }
  };

  const startRun = async () => {
    if (readOnly || !accessToken) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      // Formation uses fleet discovery (shared skill); other consoles use smart-scan.
      const path = skill === 'fleet_discovery'
        ? '/api/v1/agent/monitor/runs'
        : '/api/v1/agent/monitor/smart-scan';
      const response = await fetch(`${gateway}${withAgentCompanyQuery(path, companyId)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ skill }),
      });
      const body = await response.json() as { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : text.runError);
        return;
      }
      setMessage(text.runOk);
      await load();
    } catch {
      setError(text.runError);
    } finally {
      setBusy(false);
    }
  };

  const decide = async (runId: string, decision: 'approved' | 'rejected' | 'dismissed') => {
    if (readOnly || !accessToken) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${gateway}${withAgentCompanyQuery(`/api/v1/agent/monitor/runs/${encodeURIComponent(runId)}/decide`, companyId)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ decision }),
      });
      const body = await response.json() as { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : text.decideError);
        return;
      }
      setMessage(
        decision === 'approved'
          ? (language === 'es' ? 'Aprobado.' : 'Approved.')
          : decision === 'rejected'
            ? (language === 'es' ? 'Rechazado.' : 'Rejected.')
            : (language === 'es' ? 'Descartado.' : 'Dismissed.'),
      );
      await load();
    } catch {
      setError(text.decideError);
    } finally {
      setBusy(false);
    }
  };

  if (!accessToken || !gateway) return null;

  const stripTone = onlineCount > 0 ? 'online' : deviceCount > 0 ? 'idle' : 'offline';
  const stripLabel = stripTone === 'online'
    ? text.stripOnline
    : stripTone === 'idle'
      ? text.stripIdle
      : text.stripOffline;
  const loadingLabel = language === 'es' ? 'Cargando agente…' : 'Loading agent…';

  if (!loaded) {
    return (
      <section
        ref={sectionRef}
        className="feature-agent-gates"
        aria-labelledby={`feature-agent-gates-${feature}`}
        aria-busy="true"
      >
        <div className="feature-agent-gates__strip is-loading" role="status">
          <span className="feature-agent-gates__spinner" aria-hidden="true" />
          <div className="feature-agent-gates__strip-copy">
            <strong id={`feature-agent-gates-${feature}`}>{loadingLabel}</strong>
            <p>{language === 'es' ? 'Comprobando dispositivos y borradores…' : 'Checking devices and drafts…'}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="feature-agent-gates"
      aria-labelledby={`feature-agent-gates-${feature}`}
      aria-busy={busy}
    >
      <div className={`feature-agent-gates__strip is-${stripTone}${busy ? ' is-busy' : ''}`} role="status">
        <span className="feature-agent-gates__strip-dot" aria-hidden="true" />
        <div className="feature-agent-gates__strip-copy">
          <strong id={`feature-agent-gates-${feature}`}>{stripLabel}</strong>
          <p>
            <DodTermText text={text.stripHelp} language={language} seen={new Set()} />
          </p>
          <p className="feature-agent-gates__strip-meta">
            {deviceCount} {text.stripDevices}
            {awaiting.length ? ` · ${awaiting.length} ${text.stripWaiting}` : ''}
          </p>
        </div>
        <div className="feature-agent-gates__strip-actions">
          {!readOnly ? (
            <button
              type="button"
              className={`dashboard-btn dashboard-btn-primary${busy ? ' is-busy' : ''}`}
              disabled={busy}
              onClick={() => void startRun()}
            >
              {busy ? <span className="feature-agent-gates__spinner is-on-btn" aria-hidden="true" /> : null}
              {busy ? text.running : text.run}
            </button>
          ) : null}
          {onOpenSources ? (
            <button
              type="button"
              className="dashboard-btn"
              title={text.foldersHelp}
              onClick={() => onOpenSources()}
            >
              {text.foldersLink}
            </button>
          ) : (
            <a className="dashboard-btn" href="/console/agent#agent-folders-title" title={text.foldersHelp}>
              {text.foldersLink}
            </a>
          )}
          <a className="dashboard-btn" href="/console/agent">
            {text.openInstall}
          </a>
        </div>
      </div>
      {busy ? (
        <p className="feature-agent-gates__working" role="status" aria-live="polite">
          <span className="feature-agent-gates__spinner" aria-hidden="true" />
          {language === 'es' ? 'Trabajando… esto puede tardar un momento.' : 'Working… this can take a moment.'}
        </p>
      ) : null}
      <p className="feature-agent-gates__help feature-agent-gates__folders-hint">
        <DodTermText text={text.foldersHelp} language={language} seen={new Set()} />
      </p>

      {error ? <p className="feature-agent-gates__error" role="alert">{error}</p> : null}
      {message ? <p className="feature-agent-gates__ok" role="status">{message}</p> : null}

      <div className="feature-agent-gates__needs">
        <div className="feature-agent-gates__needs-head">
          <h4>{text.needsTitle}</h4>
          {awaiting.length ? <span className="feature-agent-gates__badge">{awaiting.length}</span> : null}
        </div>
        {awaiting.length ? (
          <ul className="feature-agent-gates__list">
            {awaiting.map((run) => {
              const taskId = checklistOpenTask(run);
              const kindLabel = run.draft?.kind === 'smart_solution_scan'
                ? (language === 'es' ? 'Soluciones' : 'Solutions')
                : run.draft?.kind === 'needs_credentials'
                  ? (language === 'es' ? 'Credenciales' : 'Credentials')
                  : '';
              return (
                <li key={run.run_id} className="feature-agent-gates__run">
                  <div className="feature-agent-gates__run-head">
                    <strong>
                      <DodTermText text={run.title || run.skill} language={language} seen={new Set()} />
                    </strong>
                    {kindLabel ? (
                      <span className="feature-agent-gates__kind-chip">{kindLabel}</span>
                    ) : null}
                  </div>
                  {run.summary ? (
                    <p>
                      <DodTermText text={shortSummary(run.summary)} language={language} seen={new Set()} />
                    </p>
                  ) : null}
                  {run.draft?.proposals?.length ? (
                    <ul className="feature-agent-gates__proposals">
                      {run.draft.proposals.slice(0, 4).map((item, idx) => (
                        <li key={`${run.run_id}-prop-${idx}`}>
                          <strong>{item.title}</strong>
                          {item.action ? ` — ${shortSummary(item.action, 120)}` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {!readOnly ? (
                    <div className="feature-agent-gates__actions">
                      {taskId && onOpenTask ? (
                        <button
                          type="button"
                          className="dashboard-btn dashboard-btn-primary"
                          disabled={busy}
                          onClick={() => onOpenTask(taskId)}
                        >
                          {text.openTask}
                        </button>
                      ) : (
                        <button type="button" className="dashboard-btn dashboard-btn-primary" disabled={busy} onClick={() => void decide(run.run_id, 'approved')}>
                          {text.approve}
                        </button>
                      )}
                      {taskId && onOpenTask ? (
                        <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void decide(run.run_id, 'approved')}>
                          {text.approve}
                        </button>
                      ) : null}
                      <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void decide(run.run_id, 'rejected')}>
                        {text.reject}
                      </button>
                      <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void decide(run.run_id, 'dismissed')}>
                        {text.dismiss}
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="feature-agent-gates__help">{text.needsEmpty}</p>
        )}
      </div>

      {recent.length ? (
        <details className="feature-agent-gates__fold">
          <summary>
            <span>{text.recentTitle}</span>
            <span className="compliance-fold-hint">{recent.length}</span>
          </summary>
          <ul className="feature-agent-gates__recent">
            {recent.map((run) => (
              <li key={`recent-${run.run_id}`}>
                <strong>
                  <DodTermText text={run.title || run.skill} language={language} seen={new Set()} />
                </strong>
                <span className={`feature-agent-gates__status-chip is-${run.status}`}>
                  {run.status}{run.decision ? ` · ${run.decision}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <details
        className="feature-agent-gates__fold compliance-agent-fold"
        open={gatesOpen}
        onToggle={(event) => setGatesOpen(event.currentTarget.open)}
      >
        <summary>
          <span>{language === 'es' ? 'Puertas y credenciales' : 'Gates and credentials'}</span>
          <span className="compliance-fold-hint">
            {language === 'es'
              ? 'Cerrado por defecto. Active solo lo que debe correr tras aprobar.'
              : 'Collapsed by default. Turn on only what should run after you approve.'}
          </span>
        </summary>
        <h4>{text.gatesTitle}</h4>
        <p className="feature-agent-gates__help">{text.gatesHelp}</p>
        {catalog.length ? (
          <ul className="feature-agent-gates__list">
            {catalog.map((entry) => {
              const modes = entry.modes?.length ? entry.modes : ['off', 'on_approval'];
              const label = language === 'es' ? (entry.label_es || entry.id) : (entry.label_en || entry.id);
              return (
                <li key={entry.id}>
                  <strong>
                    <DodTermText text={label} language={language} seen={new Set()} />
                  </strong>
                  <label className="feature-agent-gates__field">
                    <span className="visually-hidden">{label}</span>
                    <select
                      aria-label={label}
                      disabled={readOnly || busy}
                      value={permissions[entry.id] || 'off'}
                      onChange={(event) => setPermissions((current) => ({
                        ...current,
                        [entry.id]: event.target.value,
                      }))}
                    >
                      {modes.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode === 'auto' ? text.modeAuto : mode === 'on_approval' ? text.modeApproval : text.modeOff}
                        </option>
                      ))}
                    </select>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : null}
        {catalog.length && !readOnly ? (
          <div className="feature-agent-gates__actions">
            <button
              type="button"
              className="dashboard-btn dashboard-btn-primary"
              disabled={busy}
              onClick={() => void savePermissions()}
            >
              {busy ? text.saving : text.save}
            </button>
          </div>
        ) : null}

        {showCredentials ? (
          <>
            <h4>{text.credTitle}</h4>
            <p className="feature-agent-gates__help">{text.credHelp}</p>
            {!readOnly ? (
              <div className="feature-agent-gates__cred-row">
                <label className="feature-agent-gates__field">
                  <span>{text.credPortal}</span>
                  <input value={portalId} onChange={(event) => setPortalId(event.target.value)} placeholder="sprs" disabled={busy} />
                </label>
                <label className="feature-agent-gates__field">
                  <span>{text.credUser}</span>
                  <input value={portalUser} onChange={(event) => setPortalUser(event.target.value)} autoComplete="username" disabled={busy} />
                </label>
                <label className="feature-agent-gates__field">
                  <span>{text.credSecret}</span>
                  <input type="password" value={portalSecret} onChange={(event) => setPortalSecret(event.target.value)} autoComplete="new-password" disabled={busy} />
                </label>
                <button
                  type="button"
                  className="dashboard-btn"
                  disabled={busy || !portalId.trim()}
                  onClick={() => void saveCredential()}
                >
                  {text.credSave}
                </button>
              </div>
            ) : null}
            {credentials.length ? (
              <ul className="feature-agent-gates__list">
                {credentials.map((cred) => (
                  <li key={cred.portal_id}>
                    <strong>{cred.label || cred.portal_id}</strong>
                    <p>
                      {cred.username || '—'}
                      {' · '}
                      {cred.has_secret
                        ? (language === 'es' ? 'secreto guardado' : 'secret on file')
                        : (language === 'es' ? 'sin secreto' : 'no secret')}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="feature-agent-gates__help">{text.credSaved}: —</p>
            )}
          </>
        ) : null}
      </details>
    </section>
  );
}
